const DUBAI_MONTH = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' });

/** Current calendar month in Asia/Dubai as `YYYY-MM` (BR-20 — reports use Dubai periods, never UTC). */
export function currentDubaiPeriod(now: Date = new Date()): string {
  // en-CA yields "2026-09" (or "2026-09-01" style parts); keep only YYYY-MM.
  return DUBAI_MONTH.format(now).slice(0, 7);
}

export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function formatPeriodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

interface PeriodPickerProps {
  value: string;
  onChange: (period: string) => void;
  /** Latest selectable month; defaults to the current Dubai month. */
  max?: string;
  disabled?: boolean;
}

/** Spec 053 Flow C — calendar-month picker for the finance reports (Asia/Dubai). */
export default function PeriodPicker({ value, onChange, max = currentDubaiPeriod(), disabled = false }: PeriodPickerProps) {
  const atMax = value >= max;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }} role="group" aria-label="Reporting period">
      <button
        type="button"
        className="admin-btn admin-btn-sm admin-btn-outline"
        aria-label="Previous month"
        disabled={disabled}
        onClick={() => onChange(shiftPeriod(value, -1))}
      >
        ‹
      </button>
      <input
        type="month"
        aria-label="Period"
        className="admin-search-input"
        style={{ width: 'auto', minWidth: '10rem', textAlign: 'center' }}
        value={value}
        max={max}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (PERIOD_RE.test(next) && next <= max) onChange(next);
        }}
      />
      <button
        type="button"
        className="admin-btn admin-btn-sm admin-btn-outline"
        aria-label="Next month"
        disabled={disabled || atMax}
        onClick={() => onChange(shiftPeriod(value, 1))}
      >
        ›
      </button>
      <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted, #64748b)', marginLeft: '0.5rem' }}>
        {formatPeriodLabel(value)} · Asia/Dubai
      </span>
    </div>
  );
}
